import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { reservationService } from '../../services/reservationService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { 
  PlusIcon, 
  PencilIcon, 
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const ReservationList = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [reservations, setReservations] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    agencyId: '',
    fromDate: '',
    toDate: '',
    searchTerm: '',
    page: 1,
    limit: 10
  });

  useEffect(() => {
    fetchReservations();
  }, [filters]);

  const fetchReservations = async () => {
    setIsLoading(true);
    try {
      const response = await reservationService.getReservations(filters);
      
      if (response && response.success && response.data) {
        setReservations(response.data.reservations || []);
        setPagination(response.data.pagination || {
          currentPage: parseInt(filters.page),
          totalPages: Math.ceil((response.data.total || 0) / filters.limit),
          total: response.data.total || 0,
          limit: parseInt(filters.limit)
        });
      } else {
        setReservations([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          total: 0,
          limit: parseInt(filters.limit)
        });
      }
    } catch (error) {
      toast.error(t('messages.reservationsLoadError'));
      console.error('Error fetching reservations:', error);
      setReservations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filter changes
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setFilters(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  const handleStatusChange = async (newStatus, notes = '') => {
    if (!selectedReservation) return;

    setStatusUpdateLoading(true);
    try {
      await reservationService.updateReservationStatus(selectedReservation.id, {
        status: newStatus,
        notes: notes
      });

      // Local state'i güncelle
      setReservations(prev => prev.map(reservation =>
        reservation.id === selectedReservation.id
          ? { ...reservation, status: newStatus }
          : reservation
      ));

      toast.success(t('messages.statusUpdated'));
      setShowStatusModal(false);
      setSelectedReservation(null);
    } catch (error) {
      toast.error(t('messages.statusUpdateError'));
      console.error('Error updating reservation status:', error);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return t('transferStatus.pending');
      case 'confirmed':
        return t('transferStatus.confirmed');
      case 'vehicle_assigned':
        return t('transferStatus.vehicle_assigned');
      case 'on_the_way_to_pickup':
        return t('transferStatus.on_the_way_to_pickup');
      case 'arrived_at_pickup':
        return t('transferStatus.arrived_at_pickup');
      case 'passenger_on_board':
        return t('transferStatus.passenger_on_board');
      case 'completed':
        return t('transferStatus.completed');
      case 'cancelled':
        return t('transferStatus.cancelled');
      case 'cancelled_by_customer':
        return t('transferStatus.cancelled_by_customer');
      case 'cancelled_by_operator':
        return t('transferStatus.cancelled_by_operator');
      case 'cancelled_by_driver':
        return t('transferStatus.cancelled_by_driver');
      case 'no_show':
        return t('transferStatus.no_show');
      default:
        return status;
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'EUR' }).format(amount);
  };

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">{t('reservations.title')}</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/reservations/create"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            {t('reservations.newReservation')}
          </Link>
        </div>
      </div>

      {/* Filtreler - Sadece sistem kullanıcıları için */}
      {user?.role !== 'agency_user' && user?.role !== 'carrier_user' && (
        <div className="mt-4 bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              {t('fields.status')}
            </label>
            <select
              id="status"
              name="status"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">{t('common.all')}</option>
              <option value="pending">{t('transferStatus.pending')}</option>
              <option value="ready">{t('transferStatus.ready')}</option>
              <option value="in_progress">{t('transferStatus.in_progress')}</option>
              <option value="completed">{t('transferStatus.completed')}</option>
              <option value="cancelled">{t('transferStatus.cancelled')}</option>
              <option value="no_show">{t('transferStatus.no_show')}</option>
              <option value="failed">{t('transferStatus.failed')}</option>
            </select>
          </div>
          <div>
            <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700">
              {t('fields.startDate')}
            </label>
            <input
              type="date"
              id="fromDate"
              name="fromDate"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.fromDate}
              onChange={handleFilterChange}
            />
          </div>
          <div>
            <label htmlFor="toDate" className="block text-sm font-medium text-gray-700">
              {t('fields.endDate')}
            </label>
            <input
              type="date"
              id="toDate"
              name="toDate"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.toDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700">
              {t('common.search')}
            </label>
            <input
              type="text"
              id="searchTerm"
              name="searchTerm"
              placeholder={t('reservations.searchPlaceholder')}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.searchTerm}
              onChange={handleFilterChange}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setFilters({
                status: '',
                agencyId: '',
                fromDate: '',
                toDate: '',
                searchTerm: '',
                page: 1,
                limit: 10
              })}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('actions.clearFilters')}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Rezervasyon Listesi */}
      <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg">
        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : reservations.length === 0 ? (
          <div className="text-center p-12">
            <p className="text-gray-500">{t('reservations.noReservations')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.dateTime')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.fromTo')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.flightNumber')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.agency')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.passengerName')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.passengerCount')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.vehicleType')}
                  </th>
                   <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   {t('fields.paidAmount')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.totalAmount')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.notes')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('fields.reservationNumber')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('sections.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reservations.map((reservation) => {
                  const statusBgColor = {
                    'ready': 'bg-green-50',
                    'pending': 'bg-yellow-50',
                    'in_progress': 'bg-blue-50',
                    'completed': 'bg-purple-50',
                    'cancelled': 'bg-red-50',
                    'no_show': 'bg-gray-50',
                    'failed': 'bg-red-50'
                  }[reservation.status] || 'bg-gray-50';
                  
                  // Get main passenger name
                  const mainPassenger = reservation.passengers?.[0] 
                    ? `${reservation.passengers[0].first_name} ${reservation.passengers[0].last_name}`
                    : '-';
                  
                  // Get flight number from transfer info
                  const flightNumber = reservation.transfers?.[0]?.flight_number || '-';
                  
                  // Get transfer date
                  const transferDate = reservation.transfers?.[0]?.transfer_date 
                    ? formatDate(reservation.transfers[0].transfer_date) 
                    : formatDate(reservation.created_at);
                  
                  return (
                    <tr key={reservation.id} className={statusBgColor}>
                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setSelectedReservation(reservation);
                            setShowStatusModal(true);
                          }}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full hover:opacity-80 transition-opacity ${getStatusColor(reservation.status)}`}
                        >
                          {getStatusText(reservation.status)}
                        </button>
                      </td>
                      
                      {/* Date Time */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transferDate}
                      </td>
                      
                      {/* From - To */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {reservation.fromRegion?.name || '-'} - {reservation.toRegion?.name || '-'}
                      </td>
                      
                      {/* Flight No */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {flightNumber}
                      </td>
                      
                      {/* Agency */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {reservation.agency?.name || '-'}
                      </td>
                      
                      {/* Passenger Name */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {mainPassenger}
                      </td>
                      
                      {/* Passenger Count */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {reservation.passenger_count}
                      </td>
                      
                      {/* Vehicle Type */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {reservation.vehicleType?.name || '-'}
                      </td>
                      
                      {/* Paid Amount */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(reservation.paid_amount, reservation.currency)}
                      </td>
                      
                      {/* Total Amount */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(reservation.price, reservation.currency)}
                      </td>
                      
                      {/* Notes */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {reservation.payment_notes || '-'}
                      </td>
                      
                      {/* Reservation No */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {reservation.reservation_number}
                      </td>
                      
                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/reservations/${reservation.id}`}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            title={t('actions.viewDetails')}
                          >
                            <EyeIcon className="h-4 w-4" aria-hidden="true" />
                          </Link>
                          <Link
                            to={`/reservations/${reservation.id}/edit`}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            title={t('actions.edit')}
                          >
                            <PencilIcon className="h-4 w-4" aria-hidden="true" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && reservations.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('pagination.previous')}
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('pagination.next')}
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {t('pagination.showing', {
                  total: pagination.total,
                  from: (pagination.currentPage - 1) * pagination.limit + 1,
                  to: Math.min(pagination.currentPage * pagination.limit, pagination.total)
                })}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    pagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="sr-only">{t('pagination.previous')}</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {/* Page numbers */}
                {[...Array(pagination.totalPages).keys()].map((page) => (
                  <button
                    key={page + 1}
                    onClick={() => handlePageChange(page + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border ${
                      pagination.currentPage === page + 1
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    } text-sm font-medium`}
                  >
                    {page + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    pagination.currentPage === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="sr-only">{t('pagination.next')}</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('reservations.updateStatus')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('fields.reservationNumber')}: {selectedReservation?.reservation_number}
              </p>
              <div className="space-y-3">
                {[
                  { value: 'pending', label: t('transferStatus.pending'), color: 'bg-yellow-100 text-yellow-800' },
                  { value: 'confirmed', label: t('transferStatus.confirmed'), color: 'bg-blue-100 text-blue-800' },
                  { value: 'vehicle_assigned', label: t('transferStatus.vehicle_assigned'), color: 'bg-indigo-100 text-indigo-800' },
                  { value: 'on_the_way_to_pickup', label: t('transferStatus.on_the_way_to_pickup'), color: 'bg-purple-100 text-purple-800' },
                  { value: 'arrived_at_pickup', label: t('transferStatus.arrived_at_pickup'), color: 'bg-orange-100 text-orange-800' },
                  { value: 'passenger_on_board', label: t('transferStatus.passenger_on_board'), color: 'bg-teal-100 text-teal-800' },
                  { value: 'completed', label: t('transferStatus.completed'), color: 'bg-green-100 text-green-800' },
                  { value: 'cancelled', label: t('transferStatus.cancelled'), color: 'bg-red-100 text-red-800' },
                  { value: 'no_show', label: t('transferStatus.no_show'), color: 'bg-gray-100 text-gray-800' },
                  { value: 'failed', label: t('transferStatus.failed'), color: 'bg-red-100 text-red-800' }
                ].map(status => (
                  <button
                    key={status.value}
                    onClick={() => handleStatusChange(status.value)}
                    disabled={statusUpdateLoading}
                    className={`w-full text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 ${
                      selectedReservation?.status === status.value ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{status.label}</span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedReservation(null);
                  }}
                  disabled={statusUpdateLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('actions.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationList;
